export type WifiInterfaceKind = 'wifi' | 'ethernet' | 'none';

export interface WifiStatus {
  connected: boolean;
  /**
   * The Wi-Fi network's own name, whenever `wlan0` is joined to one — controller 1.7.23+.
   *
   * Two things changed there. It is the real SSID (`802-11-wireless.ssid` on the active profile) rather
   * than the NetworkManager PROFILE name, which on the Alchemy Pi read `netplan-wlan0-ORBI32`; and it is
   * populated whatever `interfaceKind` says, so a controller whose route is the cable still names the
   * Wi-Fi it is on. A hidden network legitimately has none, which is why nothing may be gated on it.
   */
  ssid: string;
  /** Which interface carries the ROUTE — not whether there is a Wi-Fi network. */
  interfaceKind: WifiInterfaceKind;
  internet: boolean;
  /**
   * Whether the controller is joined to a Wi-Fi network at all — controller 1.7.23+, and the
   * authoritative answer to that question. Absent from a 1.7.22 daemon; see `onWifiNetwork`.
   */
  wifiConnected?: boolean;
}

/**
 * Whether the controller is ON a Wi-Fi network — the one thing the Wi-Fi card and its Forget may gate on.
 *
 * Neither of the two facts beside it will do. `interfaceKind === 'wifi'` is which interface carries the
 * ROUTE, and every Alchemy-shaped install has `eth0` permanently connected for Art-Net, so its Wi-Fi was
 * invisible and its Forget button permanently dead — on the one Pi where a person most needs to change
 * the Wi-Fi. And a truthy `ssid` will not do either: a hidden network has none, and gating on it is the
 * same bug in a smaller form.
 *
 * The `interfaceKind` fallback is a RUNTIME read of an older daemon's answer, not a shape this console
 * keeps alive: the fleet is mixed until 1.7.23 has landed everywhere, and a 1.7.22 controller has no
 * other way to say it. Delete the fallback when the fleet is whole.
 */
export function onWifiNetwork(status: WifiStatus | null | undefined): boolean {
  if (!status) return false;
  if (typeof status.wifiConnected === 'boolean') return status.wifiConnected;
  return status.interfaceKind === 'wifi';
}

/**
 * The network the controller is on, as the mirror publishes it (controller 1.7.15+, mirror child
 * `network`). A remote console has no other way to learn it: `get_wifi_status` is direct-transport only
 * and deliberately stays that way, so a hosted console reads this block or nothing.
 *
 * The daemon writes all three children every time, and only these three — no address, no signal strength
 * (`internal/cloud/sync/sink.go`, `MirrorNetwork`). Every field is optional here because the block is
 * read off the wire, not constructed.
 */
export interface ControllerNetwork {
  /** `wifi` | `ethernet` | `none`. */
  kind?: string;
  /** The joined network's name. Empty unless `kind` is `wifi`. */
  ssid?: string;
  /** When the controller first observed this connection, ISO-8601 UTC. */
  since?: string;
}

/**
 * What to call that network on screen, or nothing.
 *
 * A Wi-Fi network has a name of its own and the SSID is it — data, rendered as the controller reports it.
 * A wired controller has no such name, so the route is the name. Anything else — no block, `none`, or a
 * Wi-Fi link the daemon could not name — leaves the screen with no network to state, and a screen with
 * nothing to state says nothing.
 */
export function controllerNetworkName(network: ControllerNetwork | null | undefined): string | undefined {
  if (network?.kind === 'ethernet') return 'Ethernet';
  const ssid = network?.kind === 'wifi' ? network.ssid?.trim() : '';
  return ssid || undefined;
}

const failureMessages: Record<string, string> = {
  auth: 'Wrong Wi-Fi password',
  'not-found': 'Network not found — is the controller in range of it?',
  dhcp: 'Joined, but the network gave no address',
  unsupported: "This network type isn't supported (enterprise Wi-Fi)",
};

export function wifiFailureMessage(reason: unknown): string {
  return failureMessages[String(reason ?? '')] ?? 'Could not join Wi-Fi';
}

export function validWifiPassword(password: string, openNetwork: boolean): boolean {
  return openNetwork || (password.length >= 8 && password.length <= 63);
}
